import { Link } from 'react-router-dom'

const guide = {
  slug: 'how-to-block-porn-on-wifi-router',
  category: 'device',
  kicker: 'Router · Wi-Fi',
  title: 'How to block porn on your Wi-Fi router, and the two doors it leaves open',
  seoTitle: 'How to Block Porn on a Wi-Fi Router (and Its Limits)',
  description: `Set a filtering DNS on the router and every device on your Wi-Fi is filtered at once. The steps, and why mobile data and a VPN walk straight around it.`,
  published: '2026-09-03',
  minutes: 6,
  intro: `Blocking at the router is attractive: one setting, every device in
          the house, nothing to install. It is a good layer, and this guide
          shows how to set it up in five minutes. It also says plainly what a
          router can't do, because the two doors it leaves open are exactly
          the ones a person quitting will use.`,
  next: ['dns-porn-blocker', 'porn-blocker-that-blocks-vpn', 'how-to-block-porn-on-iphone'],
  body: (
    <>
      <h2>How router-level blocking works</h2>
      <p>
        Your router hands every device on the Wi-Fi two things: an address,
        and the DNS resolver to use. Change that resolver to one that filters
        adult categories, and every phone, tablet, laptop, TV and console on
        the network asks a resolver that refuses pornography. No app on any
        device, nothing to configure per person. The mechanism itself is the
        one in <Link to="/guides/dns-porn-blocker">DNS porn blockers explained</Link>.
      </p>

      <h2>Step by step</h2>
      <ol>
        <li>Pick a filtering resolver. Several public services publish "family" or "adult content" resolver addresses meant for exactly this, and some let you create an account to choose categories. Their addresses are on their sites.</li>
        <li>Open your router's admin page (usually printed on the router: an address like <code>192.168.1.1</code>, plus a login).</li>
        <li>Find the DNS settings, typically under Internet, WAN, or DHCP/LAN. Replace the automatic or ISP resolvers with the filtering ones. If the router has both WAN and LAN DNS fields, set the LAN one: that's the one handed to devices.</li>
        <li>Save, reboot the router, and on one device forget and rejoin the Wi-Fi so it picks up the new resolver.</li>
        <li>Test: an adult site should fail to load on every device on the Wi-Fi.</li>
      </ol>
      <p>
        If your ISP's box doesn't let you change DNS, put your own router
        behind it, or set the filtering resolver on each device instead.
      </p>

      <h2>Door one: mobile data</h2>
      <p>
        The router filters the Wi-Fi. A phone that switches to 4G or 5G is on
        the carrier's network, with the carrier's resolver, and the router
        has never heard of it. One swipe in Control Center. Any router-only
        block on a device that has a SIM is a block for as long as the person
        chooses Wi-Fi.
      </p>

      <h2>Door two: a VPN, or a DNS setting on the device</h2>
      <p>
        A device can ignore the resolver the router offers. On a phone or a
        computer, the DNS field in the network settings overrides it. A VPN
        app tunnels all traffic, DNS included, to the VPN provider. Browsers
        with their own encrypted DNS (Chrome's Secure DNS, Firefox's DNS over
        HTTPS) can skip the system resolver entirely unless the resolver
        answers the canary that tells them not to. The router sees none of
        this. The full list of workarounds, and what closes each one:
        <Link to="/guides/porn-blocker-that-blocks-vpn"> a porn blocker that blocks VPNs</Link>.
      </p>

      <h2>Who a router block is for</h2>
      <p>
        A router block is a good <b>household</b> layer: it filters the smart
        TV, the console, guests' phones, the old laptop nobody manages. It's
        the right first move for parents, and there's no reason not to do it
        in a house where someone is quitting.
      </p>
      <p>
        It is not a lock for the person quitting. That person owns a phone
        with a SIM and knows the DNS field. For them the block has to live
        <em> on the device</em>, attached to something they can't undo on an
        impulse. On iPhone and iPad that's a supervised profile: the
        filtering DNS is set by the profile, the profile can't be removed,
        and adding a VPN is forbidden. Mobile data doesn't matter, because
        the filter is in the phone, not in the network. Setup is ten minutes
        once: <Link to="/guides/how-to-block-porn-on-iphone">the iPhone guide</Link>.
        On Mac and Windows, SELFCNTRL installs the filter as an app tied to
        your account, with curfews and a twenty-four-hour delay to loosen.
      </p>

      <h2>Layers, side by side</h2>
      <table>
        <thead>
          <tr><th>Layer</th><th>Covers</th><th>Bypassed by</th><th>Good for</th></tr>
        </thead>
        <tbody>
          <tr><td>Router DNS</td><td>Everything on the Wi-Fi</td><td>Mobile data, device DNS, VPN, browser DoH</td><td>The household</td></tr>
          <tr><td>Device DNS setting</td><td>That device, on any network</td><td>Changing it back, VPN, browser DoH</td><td>A quick layer</td></tr>
          <tr><td>Supervised profile (iPhone / iPad)</td><td>That device, on any network, every app</td><td>Only a request from the account, after a delay</td><td>The person quitting</td></tr>
        </tbody>
      </table>
    </>
  ),
  faq: [
    {
      q: 'Can I block porn on my Wi-Fi without touching each device?',
      a: `Yes: set a filtering DNS resolver in the router's LAN/DHCP settings
          and every device on the Wi-Fi is filtered. Devices that use mobile
          data, their own DNS setting or a VPN are not.`
    },
    {
      q: 'Does a router block work on a phone using 4G or 5G?',
      a: `No. Mobile data goes through the carrier's network, not your
          router. A block for a phone has to be on the phone.`
    },
    {
      q: 'My ISP router won\'t let me change DNS. What now?',
      a: `Either connect your own router behind the ISP's box and set DNS
          there, or set the filtering resolver directly on each device. For
          the device that matters, a supervised profile sets the resolver
          and keeps it set.`
    },
    {
      q: 'Does router blocking keep any history?',
      a: `A resolver you use answers "where is this domain?", so the
          resolver operator sees domain lookups. Whether they keep them
          depends on the service. SELFCNTRL's filtering works by category
          and keeps no browsing history; the privacy policy says exactly
          what is processed.`
    }
  ]
}

export default guide
